import { URLS } from '@/lib/constants';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { logger } from '../lib/logger';
import { supabase } from '../lib/supabase'; // Use existing Supabase instance

export class WordPressAuthService {
  private wordpressUrl = URLS.WORDPRESS_BASE;
  private ssoCookieName = process.env.EXPO_PUBLIC_SSO_COOKIE || 'sb_wp_session';

  /**
   * Check if user is authenticated via WordPress
   */
  async checkAuthentication() {
    // On web, check for WordPress SSO cookie
    if (Platform.OS === 'web') {
      const wpToken = this.getCookie(this.ssoCookieName);

      if (wpToken) {
        return await this.validateWordPressToken(wpToken);
      }
    }

    // On mobile, check stored token
    const storedToken = await AsyncStorage.getItem('wp_token');
    if (storedToken) {
      return await this.validateWordPressToken(storedToken);
    }

    return { authenticated: false };
  }

  /**
   * Validate token with WordPress backend
   */
  private async validateWordPressToken(token: string) {
    try {
      const response = await fetch(
        `${this.wordpressUrl}/wp-json/osj/v1/validate-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.valid) {
          // Store user info locally
          await this.storeUserInfo(data.user);

          return {
            authenticated: true,
            user: data.user
          };
        }
      }
    } catch (error) {
      logger.error('Failed to validate WordPress session:', error);
    }

    return { authenticated: false };
  }

  /**
   * Get user data from Supabase (read-only)
   */
  async getUserData(wpUserId: number) {
    const { data, error } = await supabase
      .from('wordpress_users')
      .select('*')
      .eq('wp_user_id', wpUserId)
      .single();

    if (error) {
      logger.error('Error fetching user data from Supabase:', error);
    }

    return data;
  }

  /**
   * Redirect to WordPress login
   */
  redirectToLogin() {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.location.href = `${this.wordpressUrl}/wp-login.php?redirect_to=${encodeURIComponent(window.location.href)}`;
      }
    } else {
      // For mobile, this would need to be handled differently
      // You might want to open a WebView or use Linking
      __DEV__ && logger.debug('Mobile WordPress login redirect not yet implemented');
    }
  }

  /**
   * Logout (redirect to WordPress logout)
   */
  async logout() {
    // Clear stored data
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('wp_user');
        localStorage.removeItem('wp_token');
      }

      // Redirect to WordPress logout
      if (typeof window !== 'undefined') {
        window.location.href = `${this.wordpressUrl}/wp-login.php?action=logout`;
      }
    } else {
      // Clear AsyncStorage
      await AsyncStorage.multiRemove(['wp_token', 'wp_user']);
    }
  }

  /**
   * Store WordPress token (for mobile)
   */
  async storeToken(token: string) {
    if (Platform.OS !== 'web') {
      await AsyncStorage.setItem('wp_token', token);
    }
  }

  // Utility functions
  private getCookie(name: string): string | null {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }

    return null;
  }

  private async storeUserInfo(user: any) {
    const userJson = JSON.stringify(user);

    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem('wp_user', userJson);
        } catch (error) {
          logger.error('Error storing user info in localStorage:', error);
        }
      }
    } else {
      try {
        await AsyncStorage.setItem('wp_user', userJson);
      } catch (error) {
        logger.error('Error storing user info in AsyncStorage:', error);
      }
    }
  }

  /**
   * Get stored WordPress user info
   */
  async getStoredUser() {
    try {
      if (Platform.OS === 'web') {
        if (typeof window !== 'undefined' && window.localStorage) {
          const userJson = localStorage.getItem('wp_user');
          return userJson ? JSON.parse(userJson) : null;
        }
      } else {
        const userJson = await AsyncStorage.getItem('wp_user');
        return userJson ? JSON.parse(userJson) : null;
      }
    } catch (error) {
      logger.error('Error getting stored user:', error);
      return null;
    }
  }

  /**
   * Notify WordPress that a course was recently watched (Continue Watching).
   * Web-only, relies on existing WordPress login cookies.
   */
  async notifyCourseActivity(wpCourseId: number) {
    try {
      if (
        !wpCourseId ||
        Platform.OS !== 'web' ||
        typeof window === 'undefined'
      ) {
        return;
      }

      const url = `${this.wordpressUrl}/wp-admin/admin-ajax.php`;

      await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `action=os_update_course_activity&course_id=${wpCourseId}`,
      });
    } catch (error) {
      logger.error('Failed to notify WordPress course activity', {
        wpCourseId,
        error,
      });
    }
  }
}

export const wpAuth = new WordPressAuthService();
